// Per-thread comments panel — moderated, name + email required (email private).
// One-level threaded replies: readers can reply to any approved top-level
// comment; replies land in `pending` alongside top-level comments.
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Loader2, ShieldCheck, MessageSquare, CornerDownRight, X, Pin, PinOff } from "lucide-react";
import axios from "axios";
import { useAuth, authHeaders } from "@/hooks/useAuth";
import MentionPill from "@/components/MentionPill";
import HelpCTA from "@/components/HelpCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

const formatDate = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-CA", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "";
    }
};

const formatError = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return "Something went wrong.";
};

// Render a comment body with `@nickname` mentions replaced by an interactive
// MentionPill (styled + hover-card with the mentioned member's public snippet).
const MENTION_RE = /(^|\s)@([a-zA-Z0-9_.-]{2,40})/g;
const renderWithMentions = (text) => {
    const parts = [];
    let lastIndex = 0;
    let m;
    let key = 0;
    while ((m = MENTION_RE.exec(text)) !== null) {
        const start = m.index + m[1].length; // skip leading whitespace
        if (start > lastIndex) parts.push(text.slice(lastIndex, start));
        parts.push(<MentionPill key={`m-${key++}`} nickname={m[2]} />);
        lastIndex = start + 1 + m[2].length; // "@" + name length
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length ? parts : text;
};

export default function ThreadPanel({ threadId, testId, label }) {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    // Top-level form state
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [body, setBody] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null);
    // Reply state — { parentId, name, email, body, submitting, status }
    const [reply, setReply] = useState(null);
    const cancelRef = useRef(false);

    // Group approved comments into { top: [...], repliesById: { parentId: [replies…] } }
    // Comments arrive oldest-first, then displayed pinned-first / newest-first at top level.
    const { topLevel, repliesByParent } = useMemo(() => {
        const top = [];
        const replies = {};
        for (const c of comments) {
            if (c.parent_id) {
                (replies[c.parent_id] ||= []).push(c);
            } else {
                top.push(c);
            }
        }
        // Pinned first, then newest-first among non-pinned.
        top.sort((a, b) => {
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;
            return b.created_at.localeCompare(a.created_at);
        });
        // replies oldest first (conversation flow)
        Object.values(replies).forEach((arr) =>
            arr.sort((a, b) => a.created_at.localeCompare(b.created_at)),
        );
        return { topLevel: top, repliesByParent: replies };
    }, [comments]);

    const remaining = 1000 - body.length;

    const load = async () => {
        try {
            const res = await axios.get(`${API}/comments`, {
                params: { thread_id: threadId },
            });
            if (!cancelRef.current) setComments(res.data || []);
        } catch {
            if (!cancelRef.current) setComments([]);
        } finally {
            if (!cancelRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        cancelRef.current = false;
        setLoading(true);
        load();
        return () => {
            cancelRef.current = true;
        };
    }, [threadId]);

    // Deep-link support: when the page opens with `?cid=<id>` (e.g. from a
    // reply notification email), find the matching comment on this thread,
    // scroll it into view, and briefly highlight it. Runs after comments
    // have loaded so the DOM node exists.
    const [highlightId, setHighlightId] = useState(null);
    useEffect(() => {
        if (loading) return;
        const params = new URLSearchParams(window.location.search);
        const cid = params.get("cid");
        if (!cid) return;
        // Only act if this comment belongs to the thread we're rendering.
        const target = comments.find((c) => c.id === cid);
        if (!target) return;
        // Wait a beat for the AnimatePresence stagger + article expand.
        const t = setTimeout(() => {
            const el = document.getElementById(`comment-${cid}`);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlightId(cid);
            const clear = setTimeout(() => setHighlightId(null), 3600);
            return () => clearTimeout(clear);
        }, 350);
        return () => clearTimeout(t);
    }, [loading, comments]);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setStatus(null);
        if (!body.trim()) {
            setStatus({ type: "error", msg: "Please add a comment." });
            return;
        }
        if (!user && (!name.trim() || !email.trim())) {
            setStatus({
                type: "error",
                msg: "Please enter your name, email, and comment (or sign in).",
            });
            return;
        }
        setSubmitting(true);
        try {
            const payload = user
                ? { thread_id: threadId, body: body.trim() }
                : {
                      thread_id: threadId,
                      name: name.trim(),
                      email: email.trim(),
                      body: body.trim(),
                  };
            const res = await axios.post(`${API}/comments`, payload, {
                headers: authHeaders(),
            });
            setStatus({
                type: "success",
                msg:
                    res.data?.message ||
                    "Thanks — your comment is awaiting moderation.",
            });
            setBody("");
        } catch (err) {
            setStatus({
                type: "error",
                msg:
                    formatError(err.response?.data?.detail) ||
                    "Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const openReply = (parent) => {
        setReply({
            parentId: parent.id,
            parentAuthor: parent.name,
            name: "",
            email: "",
            body: `@${(parent.name || "").replace(/\s+/g, "")} `,
            submitting: false,
            status: null,
        });
    };

    const submitReply = async (e) => {
        e.preventDefault();
        if (!reply || reply.submitting) return;
        setReply((r) => ({ ...r, submitting: true, status: null }));
        try {
            const payload = user
                ? {
                      thread_id: threadId,
                      body: reply.body.trim(),
                      parent_id: reply.parentId,
                  }
                : {
                      thread_id: threadId,
                      name: reply.name.trim(),
                      email: reply.email.trim(),
                      body: reply.body.trim(),
                      parent_id: reply.parentId,
                  };
            if (!payload.body) {
                setReply((r) => ({
                    ...r,
                    submitting: false,
                    status: {
                        type: "error",
                        msg: "Please add a reply.",
                    },
                }));
                return;
            }
            if (!user && (!payload.name || !payload.email)) {
                setReply((r) => ({
                    ...r,
                    submitting: false,
                    status: {
                        type: "error",
                        msg: "Please enter your name and email (or sign in).",
                    },
                }));
                return;
            }
            await axios.post(`${API}/comments`, payload, {
                headers: authHeaders(),
            });
            setReply((r) => ({
                ...r,
                submitting: false,
                status: {
                    type: "success",
                    msg: "Thanks — your reply is awaiting moderation.",
                },
                body: "",
            }));
        } catch (err) {
            setReply((r) => ({
                ...r,
                submitting: false,
                status: {
                    type: "error",
                    msg:
                        formatError(err.response?.data?.detail) ||
                        "Please try again.",
                },
            }));
        }
    };

    const isAdmin =
        user?.role === "admin" || user?.role === "super_admin";
    // When the thread is empty we render a compact "Be the first" bar
    // instead of the full form + placeholder — saves ~400px of vertical
    // space. The form expands only when the visitor asks to comment.
    const isEmpty = !loading && comments.length === 0;
    const [expandedEmpty, setExpandedEmpty] = useState(false);
    const showForm = !isEmpty || expandedEmpty;

    const togglePin = async (comment) => {
        try {
            const url = `${API}/admin/comments/${comment.id}/${comment.pinned ? "unpin" : "pin"}`;
            await axios.post(url, {}, { headers: authHeaders() });
            await load();
        } catch (err) {
            setStatus({
                type: "error",
                msg: formatError(err.response?.data?.detail),
            });
        }
    };

    return (
        <div
            data-testid={testId || `thread-${threadId}`}
            className={
                isEmpty
                    ? "mt-6 md:mt-8 pt-5 md:pt-6 border-t border-[color:var(--charcoal)]/20"
                    : "mt-12 md:mt-16 pt-10 md:pt-14 border-t border-[color:var(--charcoal)]/20"
            }
        >
            <div
                className={`flex items-center gap-3 flex-wrap ${
                    isEmpty && !expandedEmpty ? "mb-0" : "mb-8"
                }`}
            >
                <MessageSquare size={14} strokeWidth={1.6} />
                <span className="font-mono text-[12px] uppercase tracking-[0.26em]">
                    Discussion · {label || "Comments"}
                </span>
                {!isEmpty && (
                    <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                        {loading ? "…" : comments.length}{" "}
                        {comments.length === 1 ? "comment" : "comments"}
                    </span>
                )}
            </div>

            {isEmpty && !expandedEmpty && (
                <HelpCTA
                    testId={`${testId || "thread"}-help-cta`}
                    onLeaveComment={() => setExpandedEmpty(true)}
                />
            )}

            {(!isEmpty || expandedEmpty) && (
            <div className="grid grid-cols-12 gap-6 md:gap-10">
                <div className="col-span-12 lg:col-span-7">
                    {loading ? (
                        <div className="flex items-center gap-2 text-[color:var(--slate)] font-mono text-[13px] uppercase tracking-[0.2em]">
                            <Loader2 size={14} className="animate-spin" />
                            Loading discussion
                        </div>
                    ) : comments.length === 0 ? (
                        // Empty-state prompt is now rendered above the form
                        // (with the violet CTA), so nothing extra is needed
                        // inside the panel itself.
                        null
                    ) : (
                        <ul className="divide-y divide-[color:var(--charcoal)]/15">
                            <AnimatePresence initial={false}>
                                {topLevel.map((c) => {
                                    const replies = repliesByParent[c.id] || [];
                                    const isReplying =
                                        reply?.parentId === c.id;
                                    return (
                                        <motion.li
                                            key={c.id}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            transition={{
                                                duration: 0.5,
                                                ease: EASE,
                                            }}
                                            data-testid={`comment-${c.id}`}
                                            id={`comment-${c.id}`}
                                            style={{ scrollMarginTop: 96 }}
                                            className={`py-6 md:py-8 transition-colors duration-700 ${
                                                highlightId === c.id
                                                    ? "-mx-4 md:-mx-6 px-4 md:px-6 bg-amber-100/60 ring-1 ring-amber-300/70"
                                                    : c.pinned
                                                    ? "-mx-4 md:-mx-6 px-4 md:px-6 bg-[color:var(--charcoal)]/5 border-l-2 border-[color:var(--charcoal)]"
                                                    : ""
                                            }`}
                                        >
                                            {c.pinned && (
                                                <span
                                                    data-testid={`pinned-badge-${c.id}`}
                                                    className="inline-flex items-center gap-1 font-mono font-semibold text-[11px] uppercase tracking-[0.22em] text-[color:var(--charcoal)] bg-[color:var(--charcoal)] text-white px-1.5 py-0.5 mb-2"
                                                    style={{ color: "#fdfdfd" }}
                                                >
                                                    <Pin size={11} strokeWidth={1.7} />
                                                    Pinned
                                                </span>
                                            )}
                                            <div className="flex items-baseline justify-between gap-4 mb-2">
                                                <span className="font-display italic text-lg md:text-xl">
                                                    {c.name}
                                                </span>
                                                <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                                    {formatDate(c.created_at)}
                                                </span>
                                            </div>
                                            <p className="font-body text-base leading-[1.6] text-[color:var(--charcoal)]/90 whitespace-pre-line">
                                                {renderWithMentions(c.body)}
                                            </p>

                                            <div className="mt-3 flex flex-wrap items-center gap-4">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        isReplying
                                                            ? setReply(null)
                                                            : openReply(c)
                                                    }
                                                    data-testid={`reply-toggle-${c.id}`}
                                                    className="inline-flex items-center gap-1.5 font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)] hover:text-[color:var(--charcoal)] transition-colors"
                                                >
                                                    <CornerDownRight
                                                        size={13}
                                                        strokeWidth={1.6}
                                                    />
                                                    {isReplying ? "Cancel reply" : "Reply"}
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePin(c)}
                                                        data-testid={`pin-toggle-${c.id}`}
                                                        className="inline-flex items-center gap-1.5 font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)] hover:text-[color:var(--charcoal)] transition-colors"
                                                        title={
                                                            c.pinned
                                                                ? "Unpin this comment"
                                                                : "Pin to the top of the thread"
                                                        }
                                                    >
                                                        {c.pinned ? (
                                                            <>
                                                                <PinOff
                                                                    size={13}
                                                                    strokeWidth={1.6}
                                                                />
                                                                Unpin
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Pin
                                                                    size={13}
                                                                    strokeWidth={1.6}
                                                                />
                                                                Pin to top
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Existing replies */}
                                            {replies.length > 0 && (
                                                <ul
                                                    data-testid={`replies-${c.id}`}
                                                    className="mt-5 pl-4 md:pl-6 border-l-2 border-[color:var(--charcoal)]/15 space-y-5"
                                                >
                                                    {replies.map((r) => (
                                                        <li
                                                            key={r.id}
                                                            data-testid={`reply-${r.id}`}
                                                            id={`comment-${r.id}`}
                                                            style={{ scrollMarginTop: 96 }}
                                                            className={`transition-colors duration-700 rounded-sm ${
                                                                highlightId === r.id
                                                                    ? "-mx-3 px-3 py-2 bg-amber-100/60 ring-1 ring-amber-300/70"
                                                                    : ""
                                                            }`}
                                                        >
                                                            <div className="flex items-baseline justify-between gap-4 mb-1">
                                                                <span className="font-display italic text-base md:text-lg">
                                                                    {r.name}
                                                                </span>
                                                                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                                                    {formatDate(r.created_at)}
                                                                </span>
                                                            </div>
                                                            <p className="font-body text-[15px] leading-[1.55] text-[color:var(--charcoal)]/90 whitespace-pre-line">
                                                                {renderWithMentions(r.body)}
                                                            </p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}

                                            {/* Inline reply form */}
                                            <AnimatePresence>
                                                {isReplying && (
                                                    <motion.form
                                                        initial={{
                                                            opacity: 0,
                                                            height: 0,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                            height: "auto",
                                                        }}
                                                        exit={{
                                                            opacity: 0,
                                                            height: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.35,
                                                            ease: EASE,
                                                        }}
                                                        onSubmit={submitReply}
                                                        data-testid={`reply-form-${c.id}`}
                                                        className="mt-5 pl-4 md:pl-6 border-l-2 border-[color:var(--charcoal)]/40 overflow-hidden"
                                                    >
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <CornerDownRight
                                                                size={13}
                                                                strokeWidth={1.7}
                                                            />
                                                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                                                                Replying to {reply.parentAuthor}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setReply(null)
                                                                }
                                                                aria-label="Close reply"
                                                                className="ml-auto text-[color:var(--slate)] hover:text-[color:var(--charcoal)]"
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        </div>
                                                        {!user && (
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Name"
                                                                    value={reply.name}
                                                                    onChange={(e) =>
                                                                        setReply((r) => ({
                                                                            ...r,
                                                                            name: e.target.value,
                                                                        }))
                                                                    }
                                                                    data-testid={`reply-name-${c.id}`}
                                                                    className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-1.5 font-body text-sm"
                                                                    maxLength={80}
                                                                />
                                                                <input
                                                                    type="email"
                                                                    placeholder="Email (private)"
                                                                    value={reply.email}
                                                                    onChange={(e) =>
                                                                        setReply((r) => ({
                                                                            ...r,
                                                                            email: e.target.value,
                                                                        }))
                                                                    }
                                                                    data-testid={`reply-email-${c.id}`}
                                                                    className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-1.5 font-body text-sm"
                                                                />
                                                            </div>
                                                        )}
                                                        <textarea
                                                            value={reply.body}
                                                            onChange={(e) =>
                                                                setReply((r) => ({
                                                                    ...r,
                                                                    body: e.target.value,
                                                                }))
                                                            }
                                                            data-testid={`reply-body-${c.id}`}
                                                            rows={3}
                                                            maxLength={1000}
                                                            placeholder="Write your reply — use @nickname to address someone directly."
                                                            className="w-full bg-transparent border border-[color:var(--charcoal)]/30 focus:border-[color:var(--charcoal)] outline-none p-3 font-body text-sm resize-y"
                                                        />
                                                        {reply.status && (
                                                            <div
                                                                data-testid={`reply-status-${c.id}`}
                                                                className="mt-3 text-xs font-body p-2 border border-[color:var(--charcoal)] text-[color:var(--charcoal)]"
                                                            >
                                                                {reply.status.msg}
                                                            </div>
                                                        )}
                                                        <button
                                                            type="submit"
                                                            disabled={reply.submitting}
                                                            data-testid={`reply-submit-${c.id}`}
                                                            className="mt-3 pill-btn violet disabled:opacity-60"
                                                            style={{
                                                                padding: "0.5rem 1rem",
                                                                fontSize: "0.72rem",
                                                            }}
                                                        >
                                                            {reply.submitting ? (
                                                                <>
                                                                    <Loader2
                                                                        size={12}
                                                                        className="animate-spin"
                                                                    />
                                                                    Sending
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Send size={12} strokeWidth={1.7} />
                                                                    Post reply
                                                                </>
                                                            )}
                                                        </button>
                                                    </motion.form>
                                                )}
                                            </AnimatePresence>
                                        </motion.li>
                                    );
                                })}
                            </AnimatePresence>
                        </ul>
                    )}
                </div>

                <div className="col-span-12 lg:col-span-5">
                    <form
                        onSubmit={submit}
                        data-testid={`${testId || "thread"}-form`}
                        className="border border-[color:var(--charcoal)] p-6 md:p-8 bg-[color:var(--cloud)]"
                    >
                        <div className="flex items-center gap-2 mb-6">
                            <ShieldCheck size={14} strokeWidth={1.6} />
                            <span className="font-mono text-[12px] uppercase tracking-[0.24em]">
                                Add to the discussion
                            </span>
                        </div>

                        <label className="block mb-4">
                            <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                Name
                            </span>
                            <input
                                type="text"
                                value={user ? user.nickname || user.name : name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={80}
                                required={!user}
                                disabled={!!user}
                                autoComplete="name"
                                className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base disabled:opacity-70"
                            />
                        </label>
                        {!user && (
                            <label className="block mb-4">
                                <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                    Email (kept private)
                                </span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                                />
                            </label>
                        )}
                        <label className="block mb-2">
                            <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                                Comment
                            </span>
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                maxLength={1000}
                                required
                                rows={5}
                                placeholder="Use @nickname to address another commenter."
                                className="w-full bg-transparent border border-[color:var(--charcoal)]/30 focus:border-[color:var(--charcoal)] outline-none p-3 font-body text-base resize-y"
                            />
                        </label>
                        <div className="flex items-center justify-between font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-6">
                            <span>Max 1000 characters</span>
                            <span
                                className={
                                    remaining < 50
                                        ? "text-[color:var(--charcoal)]"
                                        : ""
                                }
                            >
                                {remaining}
                            </span>
                        </div>

                        {status && (
                            <div className="text-sm font-body mb-4 p-3 border border-[color:var(--charcoal)] text-[color:var(--charcoal)]">
                                {status.msg}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="thread-submit-comment"
                            className="pill-btn violet w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>
                                    <Loader2
                                        size={14}
                                        strokeWidth={1.7}
                                        className="animate-spin"
                                    />
                                    Sending
                                </>
                            ) : (
                                <>
                                    <Send size={14} strokeWidth={1.6} />
                                    Submit comment
                                </>
                            )}
                        </button>

                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--slate)] leading-relaxed">
                            Comments & replies are held for moderation before appearing.
                        </p>
                    </form>
                </div>
            </div>
            )}
        </div>
    );
}
